import { useEffect } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import RadioButton from "common/blocks/RadioButton";
import DefaultErrorView from "common/blocks/TextInput/DefaultErrorView";
import useModalContext from "common/providers/modal/useModalContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import { useForm } from "react-hook-form";
import { PartExchangeQuestions } from "services/motorpoint/partExchangeQuestions";

import { yourDetailsModal } from "..";
import Wrapper from "../common/Wrapper";
import dataEntryQuestionViewModelHelper from "../helpers/dataEntryQuestionViewModelHelper";
import styles from "./VehicleQuestionsForm.module.scss";

const cx = classNames.bind(styles);

const VehicleQuestionsForm = () => {
    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();

    const { formState, watch, register, handleSubmit, setValue } = useForm();
    const { errors } = formState;

    const { partExQuestions, formAnswers, updateFormAnswers } = usePartExchangeQuestionsContext();

    const Question = ({
        question,
        answers,
        name,
    }: {
        question: string;
        name: string;
        answers: (
            | PartExchangeQuestions.FirstResponse
            | PartExchangeQuestions.SecondResponse
            | undefined
        )[];
    }) => {
        const hasError = !!errors?.[name]?.message;

        const defaultValue =
            (formAnswers?.UserAnswers as unknown as Record<string, string>)?.[name] ?? "";

        return (
            <div className={cx("question", { hasError })}>
                {!!errors?.[name]?.message && (
                    <DefaultErrorView
                        errorText={errors?.[name]?.message as string}
                        errorClassName={cx("errorClassName")}
                    />
                )}
                <span className={cx("questionText")}>{question}</span>
                <div className={cx("options")}>
                    {answers.map((answer) =>
                        answer ? (
                            <RadioButton
                                key={answer.Identifier}
                                label={answer.Title}
                                {...register(name, {
                                    required: {
                                        message: "This information is required",
                                        value: true,
                                    },
                                })}
                                value={answer.Identifier}
                                defaultChecked={defaultValue === answer.Identifier}
                                className={cx("radioButton")}
                            />
                        ) : null
                    )}
                </div>
            </div>
        );
    };

    const questions = partExQuestions?.PartExchangeQuestionViewModels.filter(
        (question) => question.Type === "BinaryChoiceQuestionViewModel"
    ).map((question, index) => {
        const questionText = question.Title;
        const answers: (
            | PartExchangeQuestions.FirstResponse
            | PartExchangeQuestions.SecondResponse
            | undefined
        )[] = [question.FirstResponse, question.SecondResponse];
        const identifier = question.Identifier;

        return (
            <Question
                key={identifier}
                answers={answers}
                name={identifier}
                question={questionText}
            />
        );
    });

    const financeAmount = partExQuestions?.PartExchangeQuestionViewModels.filter(
        (financeAmount) => financeAmount.Type === "DataEntryQuestionViewModel"
    ).map((financeAmount) => {
        const message = financeAmount.AttributesDictionary?.["data-validation-message"] ?? "";
        const required = financeAmount.AttributesDictionary?.required === "required";

        const defaultValue = formAnswers?.UserAnswers?.outstanding_balance ?? undefined;

        return dataEntryQuestionViewModelHelper({
            errors,
            defaultValue,
            model: financeAmount,
            optionsClassName: cx("options"),
            questionTextClassName: cx("questionText"),
            questionClassName: cx("question", "financeQuestion"),
            register,
            watch,
            setValue,
            options: {
                required: {
                    message,
                    value: required,
                },
                min: {
                    message,
                    value: 0,
                },
                max: {
                    message,
                    value: 999999,
                },
                valueAsNumber: false,
            },
        });
    });

    const onSubmit = (data: unknown) => {
        updateFormAnswers("UserAnswers", data);
        openSidebar(yourDetailsModal);
    };

    useEffect(() => {
        const elementsWithError = document.getElementsByClassName(styles.hasError);

        if (elementsWithError && elementsWithError.length) {
            const sidebarContent = document.getElementById("sidebar-content")!;
            sidebarContent.scrollTop = (elementsWithError[0] as HTMLElement).offsetTop;
        }
    }, [errors]);

    return (
        <Wrapper>
            <form className={cx("questionsWrapper")} onSubmit={handleSubmit(onSubmit)}>
                <div className={cx("questionsList")}>
                    {questions}
                    {financeAmount}
                </div>
                <Button theme="primary" type="submit" fullWidth ariaLabel="Confirm">
                    Confirm
                </Button>
            </form>
        </Wrapper>
    );
};

export default VehicleQuestionsForm;
